import { getAmbrosiaLaunchMetadata } from "./ambrosiaMetadata";
import {
  getContentServiceBaseUrl,
  getGlossarySource,
  getPaperApiBaseUrl,
  getSearchUserId,
} from "./searchHosts";
import { getURLParams } from "./utils";
import {
  AutoCompleteResponse,
  BookCreateResponse,
  CsgGlossaryItem,
  CsgGlossaryResponse,
  CsgSearchResponse,
  PackageGlossary,
  SearchContext,
} from "./searchTypes";

const contextCache = new Map<string, Promise<SearchContext | null>>();
const glossaryCache = new Map<string, Promise<CsgGlossaryItem[]>>();
const packageGlossaryCache = new Map<string, Promise<PackageGlossary | null>>();

function getPearsonToken(): Promise<string> {
  return new Promise((resolve) => {
    const fromQuery = getURLParams().token;
    if (fromQuery) {
      resolve(fromQuery);
      return;
    }
    if (window.piSession?.getToken) {
      window.piSession.getToken((_status: string, token: string) => resolve(token));
      return;
    }
    resolve("");
  });
}

async function fetchBookId(cohortId: string, token: string): Promise<string | null> {
  const base = getPaperApiBaseUrl();
  const url = `${base}/nextext-api/v1/api/nextext/book/create?source=bronte_native&publicationId=${encodeURIComponent(
    cohortId,
  )}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { "X-Authorization": token },
  });
  if (!res.ok) {
    return null;
  }
  const data = (await res.json()) as BookCreateResponse;
  return data.bookId || null;
}

function buildBookIdFilterValue(bookId: string): string {
  const withoutRev = bookId.replace(/-REV$/i, "");
  if (withoutRev === bookId) {
    return bookId;
  }
  return `${bookId}|${withoutRev}`;
}

async function fetchIndexId(bookId: string, token: string): Promise<string | null> {
  const base = getContentServiceBaseUrl();
  const res = await fetch(`${base}/csg/api/v3/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Authorization": token,
      "application-id": "marin",
    },
    body: JSON.stringify({
      queryString: "*",
      advancedFilter: {
        must: [`book_id: ${buildBookIdFilterValue(bookId)}`],
      },
      startIndex: 0,
    }),
  });
  if (!res.ok) {
    return null;
  }
  const data = (await res.json()) as CsgSearchResponse;
  return data.searchResults?.[0]?.source?.indexId ?? null;
}

export async function bootstrapSearchContext(packagePath: string): Promise<SearchContext | null> {
  const cached = contextCache.get(packagePath);
  if (cached) {
    return cached;
  }

  const pending = (async (): Promise<SearchContext | null> => {
    const metadata = await getAmbrosiaLaunchMetadata(packagePath);
    if (!metadata?.cohortId) {
      return null;
    }

    const token = await getPearsonToken();
    if (!token) {
      return null;
    }

    const bookId = await fetchBookId(metadata.cohortId, token);
    if (!bookId) {
      return null;
    }

    const indexId = await fetchIndexId(bookId, token);
    if (!indexId) {
      return null;
    }

    return {
      cohortId: metadata.cohortId,
      bookId,
      indexId,
    };
  })();

  contextCache.set(packagePath, pending);
  return pending;
}

/** Test-only: drops memoised bootstrap results. */
export function clearSearchContextCache(): void {
  contextCache.clear();
  glossaryCache.clear();
  packageGlossaryCache.clear();
}

export async function fetchSearchSuggestions(
  context: SearchContext,
  queryString: string,
): Promise<string[]> {
  if (!queryString.trim()) {
    return [];
  }

  const token = await getPearsonToken();
  if (!token) {
    return [];
  }

  const base = getContentServiceBaseUrl();
  const res = await fetch(`${base}/csg/api/v1/autoSuggest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Authorization": token,
      "application-id": "ise",
    },
    body: JSON.stringify({
      queryString,
      filter: [
        `indexId:${context.indexId}`,
        `userId:${getSearchUserId()}`,
        `bookId:${context.bookId}`,
      ],
      startIndex: 0,
      responseSize: 40,
    }),
  });

  if (!res.ok) {
    return [];
  }

  const data = (await res.json()) as string[];
  return Array.isArray(data) ? data : [];
}

export async function fetchSearchResults(
  context: SearchContext,
  queryString: string,
): Promise<AutoCompleteResponse | null> {
  if (!queryString.trim()) {
    return null;
  }

  const token = await getPearsonToken();
  if (!token) {
    return null;
  }

  const base = getContentServiceBaseUrl();
  const res = await fetch(`${base}/csg/api/v4/autoComplete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Authorization": token,
      "application-id": "ise",
    },
    body: JSON.stringify({
      searchType: "FACET",
      queryString,
      filter: [
        `indexId:${context.indexId}`,
        `userId:${getSearchUserId()}`,
        `bookId:${context.bookId}`,
      ],
      groupSize: 40,
      startIndex: 0,
      requestId: crypto.randomUUID(),
      boostingKey: "pageUrn",
      boostingValue: "",
      scoreFlag: "yes",
    }),
  });

  if (!res.ok) {
    return null;
  }

  return (await res.json()) as AutoCompleteResponse;
}

async function requestGlossary(source: string, indexId: string): Promise<CsgGlossaryItem[] | null> {
  const token = await getPearsonToken();
  if (!token) {
    return null;
  }

  const base = getContentServiceBaseUrl();
  const url = `${base}/csg/api/v3/cm/glossary?source=${encodeURIComponent(
    source,
  )}&indexId=${encodeURIComponent(indexId)}`;
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { "X-Authorization": token },
    });
    if (!res.ok) {
      return null;
    }
    const data = (await res.json()) as CsgGlossaryResponse;
    return Array.isArray(data?.items) ? data.items : [];
  } catch {
    return null;
  }
}

/** Full glossary for the book; memoised per source/index, failures are retried next call. */
export async function fetchGlossary(context: SearchContext): Promise<CsgGlossaryItem[]> {
  const source = getGlossarySource();
  const cacheKey = `${source}|${context.indexId}`;
  const cached = glossaryCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const pending = requestGlossary(source, context.indexId).then((items) => {
    if (!items) {
      glossaryCache.delete(cacheKey);
      return [];
    }
    return items;
  });

  glossaryCache.set(cacheKey, pending);
  return pending;
}

/** Case-insensitive match on term or definition; term prefix hits first, then term, then definition. */
export function filterGlossary(items: CsgGlossaryItem[], queryString: string): CsgGlossaryItem[] {
  const query = queryString.trim().toLowerCase();
  if (!query) {
    return [];
  }

  const rank = (item: CsgGlossaryItem): number => {
    const term = item.term?.text?.toLowerCase() ?? "";
    if (term.startsWith(query)) {
      return 0;
    }
    if (term.includes(query)) {
      return 1;
    }
    if (item.definition?.text?.toLowerCase().includes(query)) {
      return 2;
    }
    return -1;
  };

  return items
    .map((item, index) => ({ item, index, score: rank(item) }))
    .filter(({ score }) => score >= 0)
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map(({ item }) => item);
}

/** Package `glossary.json`; `ancestry[0]` is the Glossary screen id. */
export async function getPackageGlossary(packagePath: string): Promise<PackageGlossary | null> {
  if (!packagePath) {
    return null;
  }

  const cached = packageGlossaryCache.get(packagePath);
  if (cached) {
    return cached;
  }

  const pending = (async (): Promise<PackageGlossary | null> => {
    try {
      const res = await fetch(`${packagePath.replace(/\/$/, "")}/glossary.json`);
      if (!res.ok) {
        return null;
      }
      const data = (await res.json()) as Partial<PackageGlossary>;
      if (!data?.ancestry?.length) {
        return null;
      }
      return { ancestry: data.ancestry, data: Array.isArray(data.data) ? data.data : [] };
    } catch {
      return null;
    }
  })();

  packageGlossaryCache.set(packagePath, pending);
  return pending;
}
